import type { FeatureCollection } from 'geojson';
import { LayerStats } from '@/components/maplibre/types';

function extractNumericValues(values: unknown[]): number[] {
  const numericValues: number[] = [];

  for (const value of values) {
    if (typeof value === 'number' && !isNaN(value) && isFinite(value)) {
      numericValues.push(value);
    } else if (value !== null && value !== undefined) {
      const numValue = Number(value);
      if (!isNaN(numValue) && isFinite(numValue)) {
        numericValues.push(numValue);
      }
    }
  }

  return numericValues;
}

function calculateRange(values: number[]): { min: number; max: number } | null {
  if (values.length === 0) {
    return null;
  }

  return {
    min: Math.min(...values),
    max: Math.max(...values),
  };
}

export function getPropertyRangeFromFeatureData(
  featureData: Record<string, Record<string, unknown>>,
  propertyName: string
): { min: number; max: number } | null {
  const rawValues = Object.values(featureData).map(feature => feature[propertyName]);
  const numericValues = extractNumericValues(rawValues);
  return calculateRange(numericValues);
}

export function getPropertyRangeFromGeoJSON(
  geojson: FeatureCollection,
  propertyName: string
): { min: number; max: number } | null {
  const rawValues = geojson.features.map(feature => feature.properties?.[propertyName]);
  const numericValues = extractNumericValues(rawValues);
  return calculateRange(numericValues);
}

export function getPropertyRangeFromStats(
  stats: LayerStats,
  propertyId: string
): { min: number; max: number } | null {
  if (!stats?.fields) {
    return null;
  }

  const fieldStats = stats.fields.find(field => field.id == propertyId);
  if (fieldStats && 'min' in fieldStats && 'max' in fieldStats) {
    return {
      min: fieldStats.min,
      max: fieldStats.max,
    };
  }

  return null;
}

export function getFieldStatsByColumnName(
  columnName: string,
  stats: LayerStats
): LayerStats['fields'][number] | null {
  if (!stats?.fields) return null;

  return stats.fields.find(field => field.name === columnName) || null;
}

/**
 * Reports category truncation for "showing top N of M" messaging: returns
 * rendered/total counts when the styled column has more distinct values than
 * the UI renders, null otherwise. Accepts a stats field id or column name.
 */
export function getCategoryTruncation(
  stats: LayerStats | null | undefined,
  columnIdOrName: string | number | null | undefined,
  renderedCount: number
): { rendered: number; total: number } | null {
  if (!stats?.fields || columnIdOrName == null || columnIdOrName === '') return null;
  const key = String(columnIdOrName);
  const field = stats.fields.find(f => f.id === key || f.name === key);
  if (!field?.count_distinct || field.count_distinct <= renderedCount) return null;
  return { rendered: renderedCount, total: field.count_distinct };
}

/**
 * Top-N category values by stored count. The worker writes `values` count-desc,
 * but the dict round-trips through a JSONB column, which does not preserve key
 * order (and JS iterates integer-like keys numerically) - so callers must never
 * rely on Object.keys order. Every category consumer (legend, paint expressions,
 * embeds) must select through this helper so they agree on the same top-N.
 */
export function topCategoryValues(values: Record<string, number>, limit: number): string[] {
  return Object.entries(values)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([value]) => value);
}
